/**
 * A CHARGE HAS TO SAY WHAT IT WAS FOR.
 *
 * The last of the gaps from the 2026-09-16 compliance audit: "Misc Shop
 * Supplies $25.00 is live in the book. Generically noted shop supplies
 * are prohibited outright."
 *
 * ─── WHAT THIS IS ACTUALLY ABOUT, WHICH IS NOT "SHOP SUPPLIES" ─────────
 *
 * A shop may charge for supplies. What it may not do is put a number on
 * an invoice that the customer cannot identify. So the rule this module
 * implements is not "the words shop supplies are banned" -- it is "after
 * you take away the filler, does anything remain that names a thing?"
 *
 * That distinction is the whole design, and the book proves why it
 * matters. Read 2026-09-18, eight lines mention supplies or misc:
 *
 *   FLAGGED, nothing concrete left after the filler:
 *     "Misc Shop Supplies"                        $25.00
 *     "Miscellaneous hardware and fasteners"     $207.00
 *     "Misc hardware and fittings"                $33.75
 *
 *   PASSED, because each names something:
 *     "Miscellaneous hardware, clamps, zip ties and shop supplies"
 *     "Front differential mount hardware and shop supplies"
 *     "RTV sealant, thread locker and misc hardware"
 *     "Bracket hardware, shims and misc. fasteners"
 *     "Battery terminal / charge cable hardware, dielectric grease,
 *      shop consumables"
 *
 * A rule that banned the phrase would have flagged all eight and taught
 * everybody to scroll past it. Four of the five that pass end in the
 * words "shop supplies" and are perfectly good lines: clamps, zip ties
 * and a differential mount are things a customer can picture.
 *
 * ─── THE IMPORTER IS A SOURCE OF THESE, NOT JUST TYPING ────────────────
 *
 * tools/import-shop.mjs maps another system's `shop_supplies` line type
 * straight to a fee and passes its description through, falling back to
 * the single character "—" when the source has none. That is how a line
 * nobody in this shop ever typed ends up on an estimate. "—" survives
 * the filter below with nothing left, so it flags.
 *
 * ─── IT REPORTS. IT DOES NOT BLOCK. ────────────────────────────────────
 *
 * Same call as the part condition, EPA and A/C nags, and for the same
 * reason: this is fixable by typing, the person fixing it is often not
 * the person who knows, and a gate on the money path teaches people to
 * click through warnings. The teardown is the only thing in this work
 * that refuses.
 *
 * Pure, no imports, shared with the server -- like partcondition.ts.
 */

/**
 * Words that carry no information about what was actually supplied.
 *
 * "hardware", "fasteners" and "fittings" ARE in here, deliberately. On
 * their own they name a category rather than a thing -- "miscellaneous
 * hardware and fasteners" for $207 tells a customer nothing. Put next to
 * anything concrete ("bracket hardware", "differential mount hardware")
 * the concrete word survives the filter and the line passes.
 */
const FILLER = new Set([
  "misc", "miscellaneous", "assorted", "various", "sundry", "sundries",
  "general", "other", "additional", "extra", "small", "minor",
  "shop", "supply", "supplies", "consumable", "consumables",
  "material", "materials", "stock",
  "hardware", "fastener", "fasteners", "fitting", "fittings",
  "item", "items", "part", "parts", "piece", "pieces", "bit", "bits",
  "fee", "fees", "charge", "charges", "cost", "costs",
  "and", "or", "the", "a", "an", "of", "for", "with", "plus", "etc",
  "na", "tbd", "unknown",
]);

/**
 * Whatever is left once the filler is gone.
 *
 * Punctuation goes first, so "misc." and "Misc," reduce to "misc", and
 * the importer's "—" placeholder reduces to nothing at all. Digits alone
 * do not count as a description: "misc 4" is not four of anything named.
 *
 * SINGLE LETTERS DO NOT COUNT EITHER, which is not fussiness: "n/a"
 * becomes the two words "n" and "a", and without this rule the "n"
 * survives and the line reads as described. No real description depends
 * on a lone letter -- "O ring" keeps "ring", "L bracket" keeps
 * "bracket", "M8 bolts" keeps both.
 */
export function substantiveWords(description: unknown): string[] {
  return String(description ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .split(" ")
    .filter((w) => w.length > 1 && !/^\d+$/.test(w) && !FILLER.has(w));
}

/**
 * Whether this description names nothing the customer could identify.
 *
 * An EMPTY description is not this module's problem and returns false.
 * A line with no description at all is a different fault with a
 * different message, and claiming an empty line is "generically
 * described" would send somebody looking for wording that is not there.
 */
export function isUnderDescribed(description: unknown): boolean {
  const raw = String(description ?? "").trim();
  if (!raw) return false;
  // The importer's placeholder. It is not empty and it says nothing.
  if (substantiveWords(raw).length === 0) return true;
  return false;
}

/**
 * The charges on a job that say nothing about themselves.
 *
 * Only `part` and `fee` lines, and only ones with money on them. A zero
 * line is not a charge, and labor is described by what was done rather
 * than by what was supplied -- a generic labor line is a real fault too,
 * but it is a different one and giving it this message would be wrong.
 *
 * A list rather than a boolean, so the nag can name them. Same shape as
 * partsMissingCondition() and disposalLines().
 */
export function underDescribedCharges<
  T extends { kind?: string | null; description?: string | null; total?: unknown },
>(lines: readonly T[]): T[] {
  return lines.filter((l) => {
    const kind = String(l.kind ?? "").toLowerCase();
    if (kind !== "part" && kind !== "fee") return false;
    if (!(Math.abs(Number(l.total ?? 0)) > 0.005)) return false;
    return isUnderDescribed(l.description);
  });
}

/**
 * What the screen says, or null when there is nothing to say.
 *
 * Names the lines and their money, because "$207.00 for Miscellaneous
 * hardware and fasteners" is the sentence that makes somebody go and
 * look at what those fasteners were. It also says what a good line looks
 * like, since "be more specific" is not actionable at 5pm.
 */
export function suppliesNag<
  T extends { kind?: string | null; description?: string | null; total?: unknown },
>(lines: readonly T[], money: (n: number) => string): string | null {
  const bad = underDescribedCharges(lines);
  if (!bad.length) return null;
  const named = bad
    .slice(0, 3)
    .map((l) => `${money(Number(l.total ?? 0))} for "${String(l.description ?? "").trim()}"`)
    .join(", ");
  const more = bad.length > 3 ? ` and ${bad.length - 3} more` : "";
  const one = bad.length === 1;
  return `${one ? "A charge on this job does" : `${bad.length} charges on this job do`} `
    + `not say what ${one ? "it is" : "they are"} for: ${named}${more}. `
    + `A customer has to be able to tell what they paid for, so a `
    + `bare supplies or hardware line is not enough on its own. Name what was `
    + `actually used -- "clamps, zip ties and hose" rather than "shop supplies". `
    + `You can still charge the same amount.`;
}
